import type { Sprint, Task } from '@nexo/types';
import { daysFromToday, isoDateFromToday, parseDueDate } from '../../lib/format';
import { intlLocale } from '../../lib/i18n';

export const pointsOf = (tasks: Pick<Task, 'storyPoints'>[]) =>
  tasks.reduce((sum, t) => sum + (t.storyPoints ?? 0), 0);

/** Local calendar date (`YYYY-MM-DD`) of an ISO timestamp. */
export const localDate = (iso: string) => isoDateFromToday(0, new Date(iso));

/** Every day of the sprint, `YYYY-MM-DD`, from its start to its end (both included). */
export function sprintDays(sprint: Pick<Sprint, 'startDate' | 'endDate'>): string[] {
  if (!sprint.startDate || !sprint.endDate) return [];
  const start = parseDueDate(sprint.startDate);
  const total = daysFromToday(parseDueDate(sprint.endDate), start);
  return Array.from({ length: Math.max(0, total) + 1 }, (_, i) => isoDateFromToday(i, start));
}

/** Days until the sprint ends (0 on its last day, negative once it is over). */
export function daysLeft(sprint: Pick<Sprint, 'endDate'>, now = new Date()): number | null {
  return sprint.endDate ? daysFromToday(parseDueDate(sprint.endDate), now) : null;
}

export interface BurndownPoint {
  date: string;
  /** Points still open at the end of that day (null for days still to come). */
  remaining: number | null;
  /** Straight line from everything at the start to nothing at the end. */
  ideal: number;
}

/**
 * Burndown of the sprint's current scope: each day, the story points of its tasks that were not
 * finished yet. Tasks finished before the sprint started count as done on day one.
 */
export function burndown(
  sprint: Pick<Sprint, 'startDate' | 'endDate'>,
  tasks: Pick<Task, 'storyPoints' | 'status' | 'completedAt'>[],
  today = isoDateFromToday(0),
): BurndownPoint[] {
  const days = sprintDays(sprint);
  const total = pointsOf(tasks);
  const doneOn = tasks
    .filter((t) => t.status === 'done' && t.completedAt)
    .map((t) => ({ date: localDate(t.completedAt ?? ''), points: t.storyPoints ?? 0 }));
  const last = Math.max(1, days.length - 1);
  return days.map((date, i) => ({
    date,
    remaining:
      date > today
        ? null
        : total - doneOn.filter((d) => d.date <= date).reduce((sum, d) => sum + d.points, 0),
    ideal: Math.round(total * (1 - i / last) * 10) / 10,
  }));
}

/** Average points completed over the last `count` completed sprints (null without history). */
export function averageVelocity(sprints: Sprint[], count = 3): number | null {
  const results = sprints
    .filter((s) => s.status === 'completed' && s.result)
    .sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? ''))
    .slice(0, count)
    .map((s) => s.result?.points ?? 0);
  if (results.length === 0) return null;
  return Math.round(results.reduce((a, b) => a + b, 0) / results.length);
}

/** Story points per assignee (a task with several people counts for each of them). */
export function workload(tasks: Task[]) {
  const people = new Map<
    string,
    { person: Task['assignees'][number]; points: number; done: number; tasks: number }
  >();
  for (const task of tasks) {
    for (const person of task.assignees) {
      const entry = people.get(person.id) ?? { person, points: 0, done: 0, tasks: 0 };
      entry.points += task.storyPoints ?? 0;
      entry.tasks += 1;
      if (task.status === 'done') entry.done += task.storyPoints ?? 0;
      people.set(person.id, entry);
    }
  }
  return [...people.values()].sort((a, b) => b.points - a.points || b.tasks - a.tasks);
}

let shortDate: Intl.DateTimeFormat | undefined;

/** `5 oct` / `5 Oct`, in the interface language. */
export function formatShortDate(date: string): string {
  if (shortDate?.resolvedOptions().locale !== intlLocale()) {
    shortDate = new Intl.DateTimeFormat(intlLocale(), { day: 'numeric', month: 'short' });
  }
  return shortDate.format(parseDueDate(date));
}

/** `5 oct – 16 oct`, or null when the sprint has no dates yet. */
export function sprintDates(sprint: Pick<Sprint, 'startDate' | 'endDate'>): string | null {
  if (!sprint.startDate || !sprint.endDate) return null;
  return `${formatShortDate(sprint.startDate)} – ${formatShortDate(sprint.endDate)}`;
}
